import React, { useState } from 'react';
import { Box, Button, Card, Typography, Chip, IconButton, Tooltip } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { CAMPAIGN_STATUSES, deriveStatus } from './advertiserConstants';
import { todayEastern } from '../../utils/dateUtils';
import LocalText from '../common/LocalText';
import { useLocalization } from '../../context/LocalizationContext';
import { useCurrency } from '../../context/CurrencyContext';
import ApprovalStatusBadge from '../approval/ApprovalStatusBadge';
import SubmitForApprovalButton from '../approval/SubmitForApprovalButton';
import WithdrawButton from '../approval/WithdrawButton';
import ContentPreviewDrawer from '../approval/ContentPreviewDrawer';
import { CONTENT_TYPES } from '../../constants/contentTypes';

const statusStyle = (status) => {
  const match = CAMPAIGN_STATUSES.find((s) => s.id === status);
  const color = match?.color ?? '#757575';
  return { chipBg: `${color}18`, chipColor: color, chipBorder: `${color}40` };
};

const fmt = (dateStr) => {
  if (!dateStr) return '—';
  return new Date(`${dateStr}T00:00:00`).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const fmtMoney = (v, symbol = '$', decimalPlaces = 2) =>
  v != null
    ? `${symbol}${Number(v).toLocaleString('en-US', { minimumFractionDigits: decimalPlaces, maximumFractionDigits: decimalPlaces })}`
    : null;

const viewBtnSx = {
  borderColor: '#3b82f6',
  color: '#1d4ed8',
  bgcolor: '#eff6ff',
  borderRadius: '8px',
  px: 1.5,
  py: 0.5,
  fontSize: 11,
  fontWeight: 700,
  textTransform: 'uppercase',
  letterSpacing: 0.4,
  whiteSpace: 'nowrap',
  boxShadow: 'none',
  '&:hover': { bgcolor: '#dbeafe', borderColor: '#2563eb', boxShadow: 'none' },
};

const AdvertiserCampaignCard = ({ campaign, onSelect, onEdit, onDelete, onSuccess, onView, onEditRejected, preview = false, parentLocked = false, showActions = true }) => {
  const { locale } = useLocalization();
  const { activeCurrency } = useCurrency();
  const currencySymbol = activeCurrency?.symbol ?? '$';
  const decimalPlaces  = activeCurrency?.decimalPlaces ?? 2;
  const [viewProposalOpen, setViewProposalOpen] = useState(false);

  const name =
    campaign.localizedName?.values?.find((v) => v.localeId === locale)?.value ||
    campaign.localizedName?.values?.[0]?.value ||
    '';
  const status     = campaign.status ?? deriveStatus(campaign.startDate, campaign.endDate);
  const isSnapshot    = campaign._viewType === 'snapshot';
  const isLocked      = campaign.approvalStatus === 'pending' || campaign.approvalStatus === 'rejected';
  const canDirectEdit = campaign.contentStatus === 'draft' && !isLocked;
  // An approved campaign that has started (running or ended) is part of the record: it cannot be deleted.
  const deleteLocked  = campaign.contentStatus === 'approved' && Boolean(campaign.startDate) && String(campaign.startDate).slice(0, 10) <= todayEastern();
  const isBlocked     = isLocked || parentLocked;
  const { chipBg, chipColor, chipBorder } = statusStyle(status);
  const hasCost = campaign.costPerDay > 0;

  const viewApprovalRecord = campaign.approvalId ? {
    approvalId:       campaign.approvalId,
    actionType:       campaign.approvalActionType,
    contentType:      CONTENT_TYPES.CAMPAIGN,
    sectionLabel:     'Campaign',
    contentName:      name,
    submittedByName: '',
    submittedAt:      null,
    status:            campaign.approvalStatus,
  } : null;

  const editTooltip = parentLocked
    ? 'This advertiser has a pending approval — resolve it first'
    : isLocked
      ? campaign.approvalStatus === 'pending'
        ? 'A pending proposal exists — View it or Withdraw first'
        : 'A rejected proposal exists — View it or Dismiss first'
      : canDirectEdit ? 'Edit Campaign' : 'Propose edit (requires approval)';

  const deleteTooltip = parentLocked
    ? 'This advertiser has a pending approval — resolve it first'
    : deleteLocked
      ? "This campaign has already started, so it can't be deleted"
      : isLocked
      ? campaign.approvalStatus === 'pending'
        ? 'A pending proposal exists — Withdraw it first'
        : 'A rejected proposal exists — Dismiss it first'
      : canDirectEdit ? 'Delete Campaign' : 'Propose deletion (requires approval)';

  return (
    <Card
      sx={{
        bgcolor: 'white',
        borderRadius: '16px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
        position: 'relative',
        border: '2px solid transparent',
        overflow: 'hidden',
        transition: 'all 0.2s ease',
        ...(!preview && {
          '&:hover': {
            boxShadow: '0 8px 24px rgba(10,50,109,0.15)',
            borderColor: '#5383b3',
            transform: 'translateY(-2px)',
            '& .manage-link': { color: '#0a326d' },
          },
        }),
      }}
    >
      {/* Snapshot version banner */}
      {isSnapshot && (
        <Box sx={{
          bgcolor: campaign.approvalStatus === 'pending' ? '#d97706' : '#dc2626',
          color: 'white', px: 1.5, py: 0.5, fontSize: 11, fontWeight: 600, textAlign: 'center',
          borderRadius: '14px 14px 0 0',
        }}>
          {campaign.approvalStatus === 'pending' ? 'Proposed — Pending Review' : 'Proposed — Rejected'}
        </Box>
      )}

      {/* Card body */}
      <Box
        onClick={preview || isSnapshot ? undefined : () => onSelect(campaign)}
        sx={{ p: 2.5, pb: 1.5, cursor: preview || isSnapshot ? 'default' : 'pointer' }}
      >
        {/* Status chip + Edit / Delete row */}
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={1.5} onClick={(e) => e.stopPropagation()}>
          <Chip
            label={status}
            size="small"
            sx={{ height: 22, fontSize: 10, fontWeight: 700, bgcolor: chipBg, color: chipColor, border: `1px solid ${chipBorder}`, letterSpacing: 0.3 }}
          />
          {!preview && !isSnapshot && (
            <Box display="flex" gap={0.5}>
              {isLocked ? (
                <Tooltip title="View live content (read-only)">
                  <IconButton
                    size="small"
                    onClick={(e) => { e.currentTarget.blur(); onView?.(campaign); }}
                    sx={{ color: '#3b82f6', '&:hover': { bgcolor: '#eff6ff' } }}
                  >
                    <VisibilityIcon sx={{ fontSize: 15 }} />
                  </IconButton>
                </Tooltip>
              ) : showActions ? (
                <Tooltip title={editTooltip}>
                  <span>
                    <IconButton
                      size="small"
                      disabled={parentLocked}
                      onClick={(e) => { e.currentTarget.blur(); onEdit(campaign, !canDirectEdit); }}
                      sx={{ '&:hover': { bgcolor: '#EAF0F6' }, '&.Mui-disabled': { color: '#c8d8ea' } }}
                    >
                      <EditIcon sx={{ fontSize: 15, color: parentLocked ? undefined : '#0a326d' }} />
                    </IconButton>
                  </span>
                </Tooltip>
              ) : (
                <Tooltip title="View campaign">
                  <IconButton
                    size="small"
                    onClick={(e) => { e.currentTarget.blur(); onView?.(campaign); }}
                    sx={{ color: '#3b82f6', '&:hover': { bgcolor: '#eff6ff' } }}
                  >
                    <VisibilityIcon sx={{ fontSize: 15 }} />
                  </IconButton>
                </Tooltip>
              )}
              {showActions && (
                <Tooltip title={deleteTooltip}>
                  <span>
                    <IconButton
                      size="small"
                      disabled={isBlocked || deleteLocked}
                      onClick={(e) => { e.currentTarget.blur(); onDelete(campaign, !canDirectEdit); }}
                      sx={{ '&:hover': { bgcolor: '#fff0f0' } }}
                    >
                      <DeleteIcon sx={{ fontSize: 15, color: '#d32f2f' }} />
                    </IconButton>
                  </span>
                </Tooltip>
              )}
            </Box>
          )}
        </Box>

        <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1e3a5f', lineHeight: 1.3, mb: 0.5 }}>
          {name}
        </Typography>
        <Typography variant="caption" sx={{ color: '#85a5c6', fontSize: 12 }}>
          {fmt(campaign.startDate)}
        </Typography>

        {hasCost && (
          <Box sx={{ mt: 1, bgcolor: '#f0f7f0', border: '1px solid #c8e6c9', borderRadius: '10px', px: 1.5, py: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
            <Box display="flex" alignItems="center" gap={0.5}>
              <AttachMoneyIcon sx={{ fontSize: 15, color: '#2e7d32' }} />
              <Box>
                <Typography variant="caption" sx={{ color: '#5383b3', fontSize: 9, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.3, display: 'block' }}>
                  <LocalText>Cost / Day</LocalText>
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 700, color: '#0a326d', fontSize: 13, lineHeight: 1 }}>
                  {fmtMoney(campaign.costPerDay, currencySymbol, decimalPlaces)}
                </Typography>
              </Box>
            </Box>
            <Box textAlign="right">
              <Typography variant="caption" sx={{ color: '#5383b3', fontSize: 9, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.3, display: 'block' }}>
                <LocalText>Est. Total</LocalText>
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 800, color: '#2e7d32', fontSize: 13, lineHeight: 1 }}>
                {fmtMoney(campaign.totalCost, currencySymbol, decimalPlaces)}
              </Typography>
            </Box>
          </Box>
        )}
      </Box>

      {/* Approval status row */}
      {!preview && (isSnapshot ? campaign.approvalStatus : campaign.contentStatus) && (
        <Box
          onClick={(e) => e.stopPropagation()}
          sx={{ px: 2.5, py: 1, borderTop: '1px solid #EAF0F6', display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}
        >
          {/* Badge: live cards use contentStatus; snapshot cards use approvalStatus */}
          {(isSnapshot ? campaign.approvalStatus : campaign.contentStatus) && (
            <ApprovalStatusBadge
              status={isSnapshot ? campaign.approvalStatus : campaign.contentStatus}
              actionType={campaign.approvalActionType}
            />
          )}

          {/* True draft only (no ca record): Submit for Approval */}
          {!isSnapshot && canDirectEdit && campaign.contentStatus === 'draft' && showActions && (
            <SubmitForApprovalButton contentType={CONTENT_TYPES.CAMPAIGN} contentId={campaign.id} contentName={name} onSubmitted={() => onSuccess?.('pending')} />
          )}

          {/* Snapshot: View button */}
          {isSnapshot && viewApprovalRecord && (
            <Button
              variant="outlined"
              size="small"
              startIcon={<VisibilityIcon sx={{ fontSize: 13 }} />}
              onClick={() => setViewProposalOpen(true)}
              sx={viewBtnSx}
            >
              View
            </Button>
          )}
          {/* Snapshot pending: Withdraw */}
          {isSnapshot && campaign.approvalStatus === 'pending' && campaign.approvalId && showActions && (
            <WithdrawButton approvalId={campaign.approvalId} onWithdrawn={() => onSuccess?.(campaign.approvalActionType === 'create' ? 'draft' : 'live')} />
          )}
          {/* Snapshot rejected: Edit & Resubmit + Dismiss */}
          {isSnapshot && campaign.approvalStatus === 'rejected' && campaign.approvalId && (
            <>
              {onEditRejected && (
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<EditIcon sx={{ fontSize: 13 }} />}
                  onClick={() => onEditRejected(campaign, campaign._liveId ?? campaign.id)}
                  sx={{
                    borderColor: '#0a326d', color: '#0a326d', bgcolor: '#f0f4f8',
                    borderRadius: '8px', px: 1.5, py: 0.6, fontSize: 11, fontWeight: 700,
                    textTransform: 'uppercase', letterSpacing: 0.4, whiteSpace: 'nowrap',
                    boxShadow: 'none',
                    '&:hover': { bgcolor: '#dce6f0', borderColor: '#0a326d', boxShadow: 'none' },
                  }}
                >
                  Edit & Resubmit
                </Button>
              )}
              {showActions && (
                <WithdrawButton approvalId={campaign.approvalId} onWithdrawn={() => onSuccess?.(campaign.approvalActionType === 'create' ? 'draft' : 'live')} variant="dismiss" />
              )}
            </>
          )}
        </Box>
      )}

      {/* Footer */}
      {!preview && !isSnapshot && (
        <Box
          onClick={() => onSelect(campaign)}
          sx={{ borderTop: '1px solid #EAF0F6', px: 2.5, py: 1.2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer' }}
        >
          <Typography variant="caption" sx={{ color: '#85a5c6', fontSize: 11 }}>
            <LocalText>Ends</LocalText> {fmt(campaign.endDate)}
          </Typography>
          <Box className="manage-link" display="flex" alignItems="center" gap={0.5} sx={{ color: '#5383b3', transition: 'color 0.15s ease' }}>
            <Typography variant="caption" sx={{ fontWeight: 700, fontSize: 11 }}>
              <LocalText>Manage</LocalText>
            </Typography>
            <ArrowForwardIcon sx={{ fontSize: 13 }} />
          </Box>
        </Box>
      )}

      {viewApprovalRecord && (
        <ContentPreviewDrawer
          open={viewProposalOpen}
          approvalRecord={viewApprovalRecord}
          onClose={() => setViewProposalOpen(false)}
          readOnly
          onEdit={onEditRejected ? (snap) => { setViewProposalOpen(false); onEditRejected(snap, campaign._liveId ?? campaign.id); } : undefined}
        />
      )}
    </Card>
  );
};

export default AdvertiserCampaignCard;
